import { useKeycloak } from '@react-keycloak/web';
import { Code2, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHeader } from '../components/PageHeader.js';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '../components/StatePanel.js';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table.js';
import { Textarea } from '../components/ui/textarea.js';
import { api, ApiError, type Derivation } from '../lib/api.js';
import { rootOf, useOrganisations } from '../lib/organisations.js';
import { inWorkspace, useWorkspace } from '../lib/workspace.js';

const SAMPLE_SQL =
  "SELECT parkhaus_id, avg(auslastung_pct) AS avg_auslastung_pct\nFROM parkhaus_belegung\nWHERE time > now() - interval '24 hours'\nGROUP BY parkhaus_id";

export function DerivationsPage(): JSX.Element {
  const { keycloak } = useKeycloak();
  const username =
    (keycloak.tokenParsed?.preferred_username as string | undefined) ?? 'me';

  const [allItems, setItems] = useState<Derivation[] | null>(null);
  const ws = useWorkspace();
  const orgs = useOrganisations();
  const rootId = rootOf(orgs.items)?.id;
  const items = allItems?.filter((d) => inWorkspace(ws, d.organisation_id, rootId)) ?? null;
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<Derivation | null>(null);

  function refresh(): void {
    setError(null);
    api
      .listDerivations()
      .then((res) => setItems(res.items))
      .catch((err: unknown) => {
        const msg =
          err instanceof ApiError ? `${err.code ?? 'error'}: ${err.message}` : String(err);
        setError(msg);
        setItems([]);
      });
  }

  useEffect(refresh, []);

  async function handleRefresh(id: string): Promise<void> {
    setRefreshingId(id);
    try {
      const res = await api.refreshDerivation(id);
      toast.success(
        `Refreshed: ${res.rowCount} rows · ${new Date(res.refreshed_at).toLocaleTimeString('de-DE')}`,
      );
      refresh();
    } catch (err: unknown) {
      toast.error(`Fehler: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setRefreshingId(null);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!deleteCandidate) return;
    const id = deleteCandidate.id;
    setDeleteCandidate(null);
    try {
      await api.deleteDerivation(id);
      refresh();
    } catch (err: unknown) {
      toast.error(`Fehler: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Abgeleitete Datensätze"
        lead={
          <>
            Abgeleitete Datensätze als Materialized Views im{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">derived</code>-Schema.
          </>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link to="/builder?intent=derivation">
                <Sparkles className="mr-1 h-4 w-4" />
                Per Prompt erstellen
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCreate(true)}
              title="SQL-Statement manuell eingeben (Advanced)"
            >
              <Code2 className="mr-1 h-4 w-4" />
              Manuell mit SQL
            </Button>
          </div>
        }
      />

      {error ? <ErrorPanel title="Konnte Derivations nicht laden" message={error} /> : null}
      {items === null ? <LoadingPanel /> : null}
      {items && items.length === 0 ? <EmptyPanel illustration="generic">Noch keine Derivations.</EmptyPanel> : null}

      {items && items.length > 0 ? (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Titel</TableHead>
                <TableHead>Materialized View</TableHead>
                <TableHead>Letzter Refresh</TableHead>
                <TableHead>Rows</TableHead>
                <TableHead>Output</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="align-top">
                    <div className="font-medium">{d.title}</div>
                    {d.description ? (
                      <div className="text-xs text-muted-foreground">{d.description}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    <code className="text-xs text-muted-foreground">
                      derived.{d.materialized_view_name ?? '—'}
                    </code>
                  </TableCell>
                  <TableCell className="align-top text-xs text-muted-foreground">
                    {d.last_refreshed_at
                      ? new Date(d.last_refreshed_at).toLocaleString('de-DE')
                      : '—'}
                  </TableCell>
                  <TableCell className="align-top text-xs tabular-nums">
                    {d.last_refresh_rowcount?.rowCount ?? '—'}
                  </TableCell>
                  <TableCell className="align-top text-xs">
                    {d.output_dataset_id ? (
                      <Button asChild variant="link" size="sm" className="h-auto px-0">
                        <Link to={`/catalog/${d.output_dataset_id}`}>Datensatz</Link>
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void handleRefresh(d.id)}
                        disabled={refreshingId === d.id}
                      >
                        {refreshingId === d.id ? '…' : 'Refresh'}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteCandidate(d)}
                      >
                        Löschen
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : null}

      <CreateDerivationDialog
        open={showCreate}
        ownerDefault={username}
        onOpenChange={setShowCreate}
        onCreated={() => {
          setShowCreate(false);
          refresh();
        }}
      />

      <AlertDialog
        open={deleteCandidate !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteCandidate(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Derivation wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Materialized View und Ergebnis-Datensatz werden ebenfalls entfernt.
              {deleteCandidate ? ` (${deleteCandidate.title})` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => void confirmDelete()}>
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateDerivationDialog({
  open,
  ownerDefault,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  ownerDefault: string;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}): JSX.Element {
  const [slug, setSlug] = useState('parkhaus-avg-24h');
  const [title, setTitle] = useState('Parkhausauslastung Durchschnitt 24h');
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState(ownerDefault);
  const [definition, setDefinition] = useState(SAMPLE_SQL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.createDerivation({
        slug,
        title,
        description: description || undefined,
        owner,
        language: 'sql',
        definition,
        materialization: 'materialized_view',
      });
      onCreated();
    } catch (err: unknown) {
      const msg = err instanceof ApiError ? `${err.code ?? 'error'}: ${err.message}` : String(err);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Derivation anlegen</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="der-slug">Slug</Label>
              <Input
                id="der-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="font-mono"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="der-title">Titel</Label>
              <Input
                id="der-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="der-desc">Beschreibung (optional)</Label>
            <Input
              id="der-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="der-owner">Owner</Label>
            <Input
              id="der-owner"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="der-sql">SQL-Definition</Label>
            <Textarea
              id="der-sql"
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              rows={10}
              spellCheck={false}
              className="font-mono text-xs"
              required
            />
          </div>

          {error ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Materialisiere…' : 'Materialisieren'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
